import { memo, useEffect, useMemo, useRef } from 'react';
import { sanitizeTelegramHTML } from './sanitize-html';
import { SpoilerParticleEngine } from '../../lib/spoiler-particles';

interface FormattedContentProps {
  /** HTML string from exportToTelegramHTML() */
  html: string;
}

interface SpoilerEntry {
  engine: SpoilerParticleEngine | null;
  canvas: HTMLCanvasElement | null;
  cleanup: () => void;
}

/**
 * Renders Telegram-formatted HTML content with interactive spoilers.
 *
 * Uses manual innerHTML management via ref — React never touches the
 * DOM content inside this div. This prevents React's reconciliation
 * from destroying the canvas elements that SpoilerParticleEngine
 * appends as children of tg-spoiler elements.
 *
 * Canvases are created manually and passed to the engine in external
 * mode, with dimensions measured BEFORE the canvas is appended. This
 * avoids WebKit quirks where getBoundingClientRect on inline parents
 * returns wrong values after an abs-positioned child is added.
 *
 * The HTML is sanitized before rendering to prevent XSS attacks.
 * Clicking a spoiler toggles reveal/hide.
 */
export const FormattedContent = memo(function FormattedContent({ html }: FormattedContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const entriesRef = useRef<SpoilerEntry[]>([]);

  const sanitizedHtml = useMemo(() => sanitizeTelegramHTML(html), [html]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clean up previous engines before replacing content
    for (const entry of entriesRef.current) {
      entry.cleanup();
      if (entry.engine) entry.engine.destroy();
      if (entry.canvas) entry.canvas.remove();
    }
    entriesRef.current = [];

    // Set innerHTML manually — React never touches this content.
    container.innerHTML = sanitizedHtml;

    // Defer engine creation to the next animation frame so the browser
    // has computed layout for the tg-spoiler elements.
    const rafId = requestAnimationFrame(() => {
      if (!container.isConnected) return;

      const spoilers = container.querySelectorAll('tg-spoiler');
      const entries: SpoilerEntry[] = [];

      spoilers.forEach((el) => {
        const htmlEl = el as HTMLElement;
        htmlEl.classList.add('tg-spoiler');

        // Measure dimensions BEFORE appending canvas child — this
        // gives the true element size without any abs-positioned
        // child influencing the measurement.
        const rect = htmlEl.getBoundingClientRect();

        let engine: SpoilerParticleEngine | null = null;
        let canvas: HTMLCanvasElement | null = null;

        if (rect.width > 0 && rect.height > 0) {
          canvas = document.createElement('canvas');
          canvas.style.cssText =
            'position:absolute;top:0;left:0;' +
            'pointer-events:none;border-radius:4px;';
          htmlEl.appendChild(canvas);

          // External canvas mode — we control positioning and sizing
          engine = new SpoilerParticleEngine(canvas, { mode: 'preview' });
          engine.resizeTo(rect.width, rect.height);
        }

        let permanentlyRevealed = false;
        let hovering = false;

        const handleClick = () => {
          permanentlyRevealed = !permanentlyRevealed;
          htmlEl.classList.toggle('revealed', permanentlyRevealed);

          if (permanentlyRevealed || hovering) {
            engine?.reveal(300);
          } else {
            engine?.hide(300);
          }
        };

        const handleMouseEnter = () => {
          hovering = true;
          if (!permanentlyRevealed) {
            engine?.reveal(200);
            htmlEl.classList.add('hovered');
          }
        };

        const handleMouseLeave = () => {
          hovering = false;
          htmlEl.classList.remove('hovered');
          if (!permanentlyRevealed) {
            engine?.hide(200);
          }
        };

        htmlEl.addEventListener('click', handleClick);
        htmlEl.addEventListener('mouseenter', handleMouseEnter);
        htmlEl.addEventListener('mouseleave', handleMouseLeave);

        entries.push({
          engine,
          canvas,
          cleanup: () => {
            htmlEl.removeEventListener('click', handleClick);
            htmlEl.removeEventListener('mouseenter', handleMouseEnter);
            htmlEl.removeEventListener('mouseleave', handleMouseLeave);
          },
        });
      });

      entriesRef.current = entries;
    });

    return () => {
      cancelAnimationFrame(rafId);
      for (const entry of entriesRef.current) {
        entry.cleanup();
        if (entry.engine) entry.engine.destroy();
        if (entry.canvas) entry.canvas.remove();
      }
      entriesRef.current = [];
    };
  }, [sanitizedHtml]);

  // Empty div — content is managed via ref in the useEffect above.
  return <div ref={containerRef} className="tg-message-text" />;
});
